import type { Content, Editor, JSONContent } from '@tiptap/react';
import { ExtensionsConfigOptions } from '../Editor/types';
import type { EditorState, ExternalCallbacks, ExternalReactRenderers } from '../Editor/types';
import SsrEditor from '../../server/SsrEditor';
import { PapiEditorText, PapiUser } from '@wix/da-papi-types';
import { Node } from '@tiptap/pm/model';

const getEditor = editor => {
  if (!editor) {
    return SsrEditor;
  }

  return editor;
};

export const isExtensionInstalled = (editor: Editor, name: keyof ExtensionsConfigOptions) => {
  const conf = editor.storage['da-ext-config'] ?? {};
  return conf[name] !== false;
};

export const getCurrentUser = (editor: Editor): PapiUser => {
  return editor.storage['da-currentUser'];
};

export const getExtensionOptions = <T>(editor: Editor, name: string): T => {
  const extension = editor.extensionManager.extensions.find(ext => ext.name === name);

  return extension?.options;
};

export const getExternalRenderer = (editor, key: keyof ExternalReactRenderers) => {
  const renderers = getEditor(editor).storage['da-ext-renderers'] ?? {};
  return renderers[key];
};

export const getStoredCallback = <T>(editor, key: keyof ExternalCallbacks): T | undefined => {
  const callbacks: ExternalCallbacks = editor.storage['da-callbacks'] as ExternalCallbacks;
  if (callbacks && callbacks[key]) {
    return callbacks[key] as T;
  }
  return undefined;
};

export const getPresetId = editor => {
  return editor.storage['da-presetId'];
};

export const isPapiEditorText = (value): value is PapiEditorText => {
  return (value as PapiEditorText)?.excerpt !== undefined;
};

export const isEditorState = (value): value is EditorState => {
  return (value as EditorState)?.version !== undefined;
};

export const isJsonString = (value): value is string => {
  return typeof (value as string) === 'string' && value.trim()[0] === '{';
};

export const isWriterContent = value => {
  if (isPapiEditorText(value)) {
    return value.html.type === 'writer';
  }

  return false;
};

/**
 * Content can be in a few formats, this figures out what things are
 * and returns the EditorState
 */
export const getContentFromStorage = (
  data: string | PapiEditorText | EditorState | Content | undefined
): JSONContent | undefined => {
  // Nothing stays as nothing
  if (!data) {
    return undefined;
  }

  // Server struct parse the markup
  if (isPapiEditorText(data)) {
    if (!data.html.markup) {
      return undefined;
    }
    if (data.html.type === 'writer') {
      return data.html.markup as any;
    }
    return JSON.parse(`${data.html.markup}`).document;
  }

  // Editor save state object return the document
  if (isEditorState(data)) {
    // TODO validate version
    return data.document;
  }

  // JSON string parse it
  if (isJsonString(data)) {
    return JSON.parse(data);
  }

  return data;
};

export const getNodeFromSelection = (editor): Node[] => {
  const { state } = editor;
  const { from, to } = state.selection;

  if (from === to) {
    // No range selection, single point selection
    const node = state.doc.nodeAt(from);
    return [node];
  }

  // Range selection
  const nodes: Node[] = [];
  state.doc.nodesBetween(from, to, (node, pos) => {
    nodes.push(node);
  });

  return nodes;
};

export const shouldBlockMenuBeOpen = (editor, typeName) => {
  if (editor.state.selection.empty) {
    return false;
  }
  const nodes = getNodeFromSelection(editor);

  if (nodes[0] && nodes[0].type.name === 'blockquote') {
    return nodes[1].type.name === typeName;
  }

  return nodes[0].type.name === typeName;
};

export const isBlockNodeActive = (editor: Editor, blockType) => {
  for (const key in editor.schema.nodes) {
    if (
      editor.schema.nodes[key].spec.group?.includes(blockType) &&
      editor.isActive(editor.schema.nodes[key].name)
    ) {
      return true;
    }
  }
  return false;
};

export const isElementGroupInSelection = (editor: Editor, group) => {
  const { state } = editor;
  const { from, to } = state.selection;

  let isInSelection = false;

  // Iterate over the nodes in the selection range
  state.doc.nodesBetween(from, to, node => {
    if ((node.type as any).groups.includes(group)) {
      isInSelection = true;
      return false;
    }
  });

  return isInSelection;
};

export const deleteExtension = (typeName, editor, dispatch, state) => {
  if (dispatch) {
    const { selection } = state;
    const node = (selection as any).node;

    if (!node) {
      const nodes = getNodeFromSelection(editor);
      const isDev = nodes.some(n => n.type.name === typeName);
      if (isDev) {
        const { tr } = state;
        tr.deleteRange(selection.from, selection.to);
        dispatch(tr);
      }
    }

    if (node) {
      const { tr } = state;
      tr.deleteRange(selection.from, selection.to);
      dispatch(tr);
    }
    return true;
  }
  return true;
};

export const selectionHasLinkMark = editor => {
  const { state } = editor;
  const { from, to } = state.selection;
  let hasLinkMark = false;

  state.doc.nodesBetween(from, to, node => {
    if (node.marks.some(mark => mark.type.name === 'link')) {
      hasLinkMark = true;
    }
  });

  return hasLinkMark;
};
